import { X } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';

interface Props {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string | undefined;
  /** Automation hook (default `creatable-chip-input`); derives `-input`, `-chip-{value}`, `-chip-{value}-remove-button`. */
  testId?: string;
}

/**
 * Free-entry multi-value input: existing values render as removable chips and
 * new ones are created by typing. Enter or comma commits, Backspace on an empty
 * field removes the last chip, and blur commits so a half-typed value isn't lost
 * when the user clicks Save. Values are free text — there is no option list to
 * pick from, which is the point.
 */
export function CreatableChipInput({
  values,
  onChange,
  placeholder = 'Type and press Enter',
  disabled = false,
  className,
  testId = 'creatable-chip-input',
}: Props) {
  const [draft, setDraft] = useState('');

  /** Commits the draft, ignoring blanks and exact duplicates. */
  function commit() {
    const next = draft.trim();
    if (!next) return;
    if (!values.includes(next)) onChange([...values, next]);
    setDraft('');
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
      return;
    }
    if (e.key === 'Backspace' && draft === '' && values.length > 0) {
      e.preventDefault();
      onChange(values.slice(0, -1));
    }
  }

  return (
    <div
      className={cn(
        'focus-within:ring-ring flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border bg-white px-2 py-1.5 focus-within:ring-1',
        disabled && 'cursor-not-allowed opacity-60',
        className,
      )}
      data-testid={testId}
    >
      {values.map((value, i) => (
        <Badge
          // Duplicates are prevented on entry, but a value arriving from the API
          // could repeat — index keeps the key unique either way.
          key={`${value}-${String(i)}`}
          variant="outline"
          data-testid={`${testId}-chip-${value}`}
          className="gap-1 rounded-md border-blue-200 bg-blue-50 py-0.5 pr-0.5 pl-2 text-xs font-medium text-blue-700"
        >
          {value}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label={`Remove ${value}`}
            data-testid={`${testId}-chip-${value}-remove-button`}
            onClick={() => {
              onChange(values.filter((_, index) => index !== i));
            }}
            className={cn(
              'size-4 rounded-sm p-0 text-blue-700 hover:bg-blue-100',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
          >
            <X className="size-3" />
          </Button>
        </Badge>
      ))}
      <input
        type="text"
        data-testid={`${testId}-input`}
        value={draft}
        disabled={disabled}
        placeholder={values.length === 0 ? placeholder : ''}
        onChange={(e) => {
          setDraft(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        onBlur={commit}
        className={cn(
          'placeholder:text-muted-foreground min-w-32 flex-1 bg-transparent text-sm outline-none',
          disabled && 'cursor-not-allowed',
        )}
      />
    </div>
  );
}
